// The sign-in pages' frame (login, sign-up, the forced password change), dressed as the public website: a lifestyle
// photograph beside the form from 1024px, a slim photo band over a single column on phones and tablets.
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Logo } from './site/Logo';
import { INTERIORS } from '../lib/site';

const i = (n: number) => ({ '--i': n } as CSSProperties);

export function AuthShell({ title, subtitle, children, below, image = INTERIORS.hero }: {
  title: ReactNode; subtitle?: ReactNode; children: ReactNode;
  /** Links under the form. */ below?: ReactNode;
  /** The photograph (a /public path). */ image?: string;
}) {
  const back = (cls: string) => (
    <Link to="/" className={`group inline-flex items-center gap-2 text-[13px] font-medium tracking-wide transition-colors duration-300 ${cls}`}>
      <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-0.5" /> Website
    </Link>
  );

  return (
    <div className="min-h-[100svh] overflow-x-clip bg-white font-sans text-ink-800 lg:grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      {/* Photograph: a band on top below 1024px, the full-height left half above. */}
      <aside className="relative h-[200px] overflow-hidden bg-ink-900 sm:h-[260px] lg:sticky lg:top-0 lg:h-[100svh]">
        <img src={image} alt="" className="rise-zoom absolute inset-0 h-full w-full object-cover" fetchPriority="high" draggable={false} />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/65 via-ink-900/15 to-ink-900/70" />
        <div className="relative z-10 flex h-full flex-col justify-between p-6 sm:p-10 lg:p-14 xl:p-16">
          <div className="rise-fade flex items-center justify-between gap-4" style={i(1)}>
            <Logo tone="white" className="h-9 sm:h-10" />
            {back('text-white/80 hover:text-white lg:hidden')}
          </div>
          <div className="hidden lg:block">
            <p className="rise eyebrow !text-bronze-300" style={i(1)}>Greater London · Dubai</p>
            <p className="rise h-section mt-5 max-w-[14ch] text-white" style={i(2)}>Homes Designed for <em>Modern Renters</em></p>
          </div>
        </div>
      </aside>

      {/* The form. */}
      <main className="relative -mt-6 flex min-h-[calc(100svh-176px)] flex-col rounded-t-[22px] bg-white px-6 sm:-mt-8 sm:min-h-[calc(100svh-228px)] sm:rounded-t-[28px] sm:px-10 lg:mt-0 lg:min-h-[100svh] lg:rounded-none lg:px-16">
        <div className="hidden justify-end pt-10 lg:flex">{back('rise-fade text-ink-500 hover:text-ink-900')}</div>
        <div className="mx-auto my-auto w-full max-w-[420px] py-10 sm:py-14 lg:py-16">
          <h1 className="rise font-display text-[2.5rem] font-medium leading-[1.05] tracking-[-0.02em] text-ink-900 [text-wrap:balance] sm:text-[3rem]" style={i(0)}>{title}</h1>
          {subtitle && <div className="rise mt-3 text-[15px] leading-relaxed text-ink-500" style={i(1)}>{subtitle}</div>}
          <div className="rise mt-8 sm:mt-10" style={i(2)}>{children}</div>
          {below && <div className="rise mt-10 border-t border-ink-200/70 pt-6 text-[14px] leading-relaxed text-ink-500" style={i(3)}>{below}</div>}
        </div>
      </main>
    </div>
  );
}

/** The site's full-width submit: the charcoal pill, with a quiet disabled state. */
export function AuthSubmit({ children, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="submit" className="pill group w-full disabled:pointer-events-none disabled:opacity-60" {...rest}>
      <span>{children}</span>
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
    </button>
  );
}

/** A form-level message (error or success) in the site's quiet style. */
export function AuthNote({ tone = 'error', children }: { tone?: 'error' | 'ok'; children: ReactNode }) {
  return (
    <p role={tone === 'error' ? 'alert' : 'status'} className={`rounded-[10px] border px-4 py-3 text-[14px] leading-relaxed ${tone === 'error' ? 'border-red-200 bg-red-50/70 text-red-800' : 'border-emerald-200 bg-emerald-50/70 text-emerald-800'}`}>
      {children}
    </p>
  );
}
